import React, { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { RotateCw, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';
import { streamManager } from '../lib/streamManager';
import { HEAL } from '../lib/scrcpyStream';
import { noteText, isPcReason } from '../lib/monitorNotes';

/*
 * Monitor video self-heal, V3: the honest state of ONE live tile, drawn over its canvas.
 * (docs/MONITOR-VIDEO-SELFHEAL-DESIGN.md section 3.)
 *
 * A tile never says "Live" over a black picture again: it says what the socket and the phone are
 * actually doing - connecting, waiting for the phone (with the PC's own reason when the server
 * relays one), starting the picture, streaming (or "screen unchanged" for a static screen whose
 * liveness check was answered), no picture, retrying in N s, or off with a Retry button.
 *
 * The state comes from lib/streamManager.js's store through useSyncExternalStore, keyed by the
 * stream key, so a state change re-renders this chip only - never the card, never the canvas.
 */

const TONE = {
  grey: 'bg-background/85 text-muted-foreground ring-border',
  neutral: 'bg-background/85 text-foreground/80 ring-border',
  green: 'bg-success/20 text-success ring-success/40',
  amber: 'bg-warning/20 text-warning ring-warning/40',
  red: 'bg-danger/20 text-danger ring-danger/40',
};

const stop = (e) => e.stopPropagation();

/** The status of one stream key (null when the key has no live stream). */
export function useStreamStatus(streamKey) {
  const subscribe = useCallback(
    (cb) => (streamKey ? streamManager.subscribe(streamKey, cb) : () => {}),
    [streamKey],
  );
  const get = useCallback(() => (streamKey ? streamManager.getStatus(streamKey) : null), [streamKey]);
  return useSyncExternalStore(subscribe, get, get);
}

function useTicking(active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

const secs = (ms) => Math.max(0, Math.round(ms / 1000));

/**
 * Words and tone for a status. Pure, exported for tests.
 * @returns {{tone:string, text:string, sub:string|null, title:string}|null}
 */
export function describeStatus(st, pc, now = Date.now()) {
  if (!st || st.state === 'idle' || st.state === 'stopped') return null;
  const note = st.note ? noteText(st.note, pc) : '';
  const tipParts = [];
  let out;
  switch (st.state) {
    case 'connecting':
      out = { tone: 'grey', text: st.reason || 'Connecting...', sub: null };
      break;
    case 'waiting':
      out = { tone: 'grey', text: 'Waiting for the phone...', sub: note || null };
      break;
    case 'starting':
      out = { tone: 'grey', text: 'Starting the picture...', sub: note || null };
      break;
    case 'streaming':
      out = st.unchanged
        ? { tone: 'neutral', text: 'Streaming · Screen unchanged', sub: null }
        : { tone: 'green', text: 'Streaming', sub: null };
      break;
    case 'no-picture':
      out = {
        tone: 'amber',
        text: `No new picture for ${secs(now - (st.silentSince || st.since))} s`,
        sub: st.reason || note || null,
      };
      break;
    case 'retrying': {
      const left = st.nextAt ? secs(st.nextAt - now) : null;
      const count = st.phase === 'slow'
        ? `slow ${st.slowTries}/${HEAL.SLOW_TRIES}`
        : `${st.attempt}/${HEAL.FAST_DELAYS_MS.length}`;
      out = {
        tone: 'amber',
        text: left != null ? `Retrying in ${left} s (${count})` : `Retrying (${count})`,
        sub: st.reason || null,
      };
      break;
    }
    case 'paused':
      out = { tone: 'red', text: `Video off: ${st.reason || 'stopped'}`, sub: null };
      break;
    default:
      return null;
  }
  if (st.reason) tipParts.push(st.reason);
  if (note && note !== st.reason) tipParts.push(`${pc || 'PC'}: ${note}`);
  if (st.note && st.note.reason) tipParts.push(`reason from the PC: ${st.note.reason}`);
  if (st.closeCode != null) tipParts.push(`last close code ${st.closeCode}`);
  tipParts.push(st.route === 'remote' ? 'route: through the server' : st.route === 'local' ? 'route: direct from this PC' : `route: ${st.route}`);
  out.title = tipParts.join(' | ');
  return out;
}

/**
 * @param {object} props
 * @param {string} props.streamKey
 * @param {string} [props.pc]           the PC's name, for the words
 * @param {boolean} [props.repairEnabled] offer "Repair video on {pc}" when the reason came from the PC
 * @param {number} [props.repairBlockedUntil] ms epoch; the PC was just repaired
 * @param {() => void} [props.onRepair]
 * @param {'tile'|'dialog'} [props.variant]
 */
export default function StreamStateChip({ streamKey, pc, repairEnabled, repairBlockedUntil, onRepair, variant = 'tile' }) {
  const st = useStreamStatus(streamKey);
  const ticking = !!st && (st.state === 'retrying' || st.state === 'no-picture' || (repairBlockedUntil || 0) > Date.now());
  const now = useTicking(ticking);
  const d = describeStatus(st, pc, now);
  if (!d) return null;
  const stale = st.everPicture && st.state !== 'streaming';
  const canRepair = repairEnabled && st.state === 'paused' && isPcReason(st) && typeof onRepair === 'function';
  const repairWait = repairBlockedUntil && repairBlockedUntil > now ? secs(repairBlockedUntil - now) : 0;

  return (
    <>
      {stale && (
        // The last picture stays visible, dimmed, so a stale frame is never mistaken for live.
        <div className="pointer-events-none absolute inset-0 z-10 bg-black/45" data-testid="stream-stale" />
      )}
      <div
        className={cn(
          'absolute z-40 flex flex-col items-center gap-1',
          variant === 'dialog' ? 'left-3 right-3 bottom-3' : 'left-1.5 right-1.5 bottom-1.5',
        )}
        onClick={st.state === 'paused' ? stop : undefined}
      >
        <div
          className={cn('max-w-full rounded-lg px-2 py-1 text-center ring-1 backdrop-blur-sm', TONE[d.tone])}
          title={d.title}
          data-testid="stream-chip"
          data-state={st.state}
          data-route={st.route}
        >
          <p className="text-[10px] font-medium leading-tight">{d.text}</p>
          {d.sub && <p className="mt-0.5 text-[9px] leading-tight opacity-80 line-clamp-3">{d.sub}</p>}
        </div>
        {st.state === 'paused' && (
          <div className="flex flex-wrap items-center justify-center gap-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); streamManager.retry(streamKey); }}
              className="inline-flex items-center gap-1 rounded-full bg-background/90 px-2 py-0.5 text-[10px] font-medium text-foreground ring-1 ring-border hover:text-primary"
              data-testid="stream-retry"
            >
              <RotateCw className="h-3 w-3" /> Retry
            </button>
            {canRepair && (
              <button
                type="button"
                disabled={repairWait > 0}
                onClick={(e) => { e.stopPropagation(); onRepair(); }}
                title={repairWait > 0 ? `Repaired recently; try again in ${repairWait} s` : undefined}
                className="inline-flex items-center gap-1 rounded-full bg-background/90 px-2 py-0.5 text-[10px] font-medium text-foreground ring-1 ring-border hover:text-primary disabled:opacity-50"
                data-testid="stream-repair"
              >
                <Wrench className="h-3 w-3" /> Repair video on {pc || 'the PC'}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
